import {
  ARMOR_BREAK_DURATION_LV,
  ARMOR_BREAK_FRACTURE_MAGNITUDE,
  ARMOR_BREAK_MAGNITUDE_LV,
  BURN_DPS,
  BURN_DURATION,
  FROST_DURATION,
  FROST_SLOW,
  POISON_DPS,
  POISON_DURATION,
  POISON_SLOW
} from '../../model/effects'
import type { TowerDefinition, TowerLevelDefinition } from '../../model/towers'
import type { TowerDefinitionV2 } from './types'

function mageArmorBreak(levelIndex: number): {
  id: 'armorBreak'
  duration: number
  slow: 0
  damagePerSecond: 0
  armorBreakMagnitude: number
} {
  const duration = ARMOR_BREAK_DURATION_LV[levelIndex] ?? ARMOR_BREAK_DURATION_LV[0]
  const magnitude =
    levelIndex >= 3
      ? ARMOR_BREAK_FRACTURE_MAGNITUDE
      : (ARMOR_BREAK_MAGNITUDE_LV[levelIndex] ?? ARMOR_BREAK_MAGNITUDE_LV[0])
  return {
    id: 'armorBreak',
    duration,
    slow: 0,
    damagePerSecond: 0,
    armorBreakMagnitude: magnitude
  }
}

const FIRE_LEVELS: TowerLevelDefinition[] = [
  {
    cost: 50,
    range: 4.5,
    damage: 25,
    attackInterval: 0.8,
    projectileSpeed: 10,
    splashRadius: 0,
    combatEnabled: true,
    effects: []
  },
  {
    cost: 75,
    range: 5,
    damage: 40,
    attackInterval: 0.7,
    projectileSpeed: 11,
    splashRadius: 0,
    combatEnabled: true,
    effects: []
  },
  {
    cost: 120,
    range: 5.5,
    damage: 65,
    attackInterval: 0.6,
    projectileSpeed: 12,
    splashRadius: 0,
    combatEnabled: true,
    effects: []
  },
  {
    cost: 190,
    range: 6,
    damage: 95,
    attackInterval: 0.5,
    projectileSpeed: 13,
    splashRadius: 0,
    combatEnabled: true,
    effects: [{ id: 'burn', duration: BURN_DURATION, slow: 0, damagePerSecond: BURN_DPS }]
  }
]

const FROST_LEVELS: TowerLevelDefinition[] = [
  {
    cost: 60,
    range: 4.5,
    damage: 10,
    attackInterval: 1.1,
    projectileSpeed: 9,
    splashRadius: 1,
    combatEnabled: true,
    effects: [{ id: 'frost', duration: FROST_DURATION, slow: FROST_SLOW, damagePerSecond: 0 }]
  },
  {
    cost: 90,
    range: 5,
    damage: 16,
    attackInterval: 1,
    projectileSpeed: 10,
    splashRadius: 1.2,
    combatEnabled: true,
    effects: [{ id: 'frost', duration: 3, slow: 0.22, damagePerSecond: 0 }]
  },
  {
    cost: 140,
    range: 5.5,
    damage: 25,
    attackInterval: 0.9,
    projectileSpeed: 11,
    splashRadius: 1.4,
    combatEnabled: true,
    effects: [{ id: 'frost', duration: 3.5, slow: 0.25, damagePerSecond: 0 }]
  },
  {
    cost: 220,
    range: 6,
    damage: 36,
    attackInterval: 0.8,
    projectileSpeed: 12,
    splashRadius: 1.6,
    combatEnabled: true,
    effects: [{ id: 'frost', duration: 4, slow: 0.28, damagePerSecond: 0 }]
  }
]

const CANNON_LEVELS: TowerLevelDefinition[] = [
  {
    cost: 80,
    range: 5,
    damage: 50,
    attackInterval: 1.8,
    projectileSpeed: 6,
    splashRadius: 1.5,
    combatEnabled: true,
    effects: []
  },
  {
    cost: 120,
    range: 5.3,
    damage: 80,
    attackInterval: 1.65,
    projectileSpeed: 6.5,
    splashRadius: 1.8,
    combatEnabled: true,
    effects: []
  },
  {
    cost: 180,
    range: 5.6,
    damage: 125,
    attackInterval: 1.5,
    projectileSpeed: 7,
    splashRadius: 2.1,
    combatEnabled: true,
    effects: []
  },
  {
    cost: 280,
    range: 6,
    damage: 180,
    attackInterval: 1.35,
    projectileSpeed: 7.5,
    splashRadius: 2.4,
    combatEnabled: true,
    effects: []
  }
]

const POISON_LEVELS: TowerLevelDefinition[] = [
  {
    cost: 65,
    range: 4.5,
    damage: 5,
    attackInterval: 1.2,
    projectileSpeed: 9,
    splashRadius: 0,
    combatEnabled: true,
    effects: [
      { id: 'poison', duration: POISON_DURATION, slow: POISON_SLOW, damagePerSecond: POISON_DPS }
    ]
  },
  {
    cost: 95,
    range: 5,
    damage: 8,
    attackInterval: 1.1,
    projectileSpeed: 10,
    splashRadius: 0,
    combatEnabled: true,
    effects: [{ id: 'poison', duration: 4.5, slow: 0.27, damagePerSecond: 12 }]
  },
  {
    cost: 145,
    range: 5.5,
    damage: 12,
    attackInterval: 1,
    projectileSpeed: 11,
    splashRadius: 0,
    combatEnabled: true,
    effects: [{ id: 'poison', duration: 5, slow: 0.3, damagePerSecond: 18 }]
  },
  {
    cost: 230,
    range: 6,
    damage: 18,
    attackInterval: 0.9,
    projectileSpeed: 12,
    splashRadius: 0,
    combatEnabled: true,
    effects: [
      { id: 'poison', duration: 5.5, slow: 0.32, damagePerSecond: 25, contagious: true }
    ]
  }
]

/**
 * ACTIVE tower catalog V2.
 * TargetClass: FIRE/FROST/POISON → BOTH, CANNON → GROUND, AA → AIR.
 */
export const TOWER_DEFINITIONS_V2: Record<string, TowerDefinitionV2> = {
  fire: {
    id: 'fire',
    displayName: 'Fire',
    damageType: 'ENERGY',
    targetClass: 'BOTH',
    color: 0xef4444,
    letter: 'F',
    footprint: { width: 2, height: 2 },
    visualId: 'fire',
    levels: FIRE_LEVELS
  },
  frost: {
    id: 'frost',
    displayName: 'Frost',
    damageType: 'COLD',
    targetClass: 'BOTH',
    color: 0x3b82f6,
    letter: 'I',
    footprint: { width: 2, height: 2 },
    visualId: 'frost',
    levels: FROST_LEVELS
  },
  cannon: {
    id: 'cannon',
    displayName: 'Cannon',
    damageType: 'EXPLOSIVE',
    targetClass: 'GROUND',
    color: 0x111827,
    letter: 'C',
    footprint: { width: 2, height: 2 },
    visualId: 'cannon',
    levels: CANNON_LEVELS
  },
  poison: {
    id: 'poison',
    displayName: 'Poison',
    damageType: 'TOXIC',
    targetClass: 'BOTH',
    color: 0xa3e635,
    letter: 'P',
    footprint: { width: 2, height: 2 },
    visualId: 'poison',
    levels: POISON_LEVELS
  },
  aa: {
    id: 'aa',
    displayName: 'AA',
    damageType: 'PIERCE',
    targetClass: 'AIR',
    color: 0x22d3ee,
    letter: 'A',
    footprint: { width: 2, height: 2 },
    visualId: 'aa',
    levels: [
      {
        cost: 70,
        range: 6.5,
        damage: 22,
        attackInterval: 0.55,
        projectileSpeed: 14,
        splashRadius: 0,
        combatEnabled: true,
        effects: []
      },
      {
        cost: 105,
        range: 7,
        damage: 34,
        attackInterval: 0.48,
        projectileSpeed: 15,
        splashRadius: 0,
        combatEnabled: true,
        effects: []
      },
      {
        cost: 160,
        range: 7.5,
        damage: 52,
        attackInterval: 0.42,
        projectileSpeed: 16,
        splashRadius: 0,
        combatEnabled: true,
        effects: []
      },
      {
        cost: 240,
        range: 8.2,
        damage: 74,
        attackInterval: 0.38,
        projectileSpeed: 17,
        splashRadius: 0,
        combatEnabled: true,
        effects: [],
        multiLockTargets: 2
      }
    ]
  },
  /**
   * Mage — single-target ARCANE support. Primary value is Armor Break (DEBUFF),
   * not personal DPS. LV4 FRACTURE uses a stronger temporary magnitude (no AoE).
   */
  mage: {
    id: 'mage',
    displayName: 'Mage',
    damageType: 'ARCANE',
    targetClass: 'BOTH',
    color: 0xc084fc,
    letter: 'M',
    footprint: { width: 2, height: 2 },
    visualId: 'mage',
    levels: [
      {
        cost: 55,
        range: 5,
        damage: 8,
        attackInterval: 1.05,
        projectileSpeed: 11,
        splashRadius: 0,
        combatEnabled: true,
        effects: [mageArmorBreak(0)]
      },
      {
        cost: 85,
        range: 5.4,
        damage: 12,
        attackInterval: 0.95,
        projectileSpeed: 12,
        splashRadius: 0,
        combatEnabled: true,
        effects: [mageArmorBreak(1)]
      },
      {
        cost: 130,
        range: 5.8,
        damage: 18,
        attackInterval: 0.88,
        projectileSpeed: 13,
        splashRadius: 0,
        combatEnabled: true,
        effects: [mageArmorBreak(2)]
      },
      {
        cost: 200,
        range: 6.2,
        damage: 26,
        attackInterval: 0.8,
        projectileSpeed: 14,
        splashRadius: 0,
        combatEnabled: true,
        effects: [mageArmorBreak(3)]
      }
    ]
  }
}

export function materializeTowerDefinition(def: TowerDefinitionV2): TowerDefinition {
  return {
    id: def.id,
    name: def.displayName,
    color: def.color,
    letter: def.letter,
    footprint: def.footprint,
    levels: def.levels,
    damageType: def.damageType,
    targetClass: def.targetClass,
    visualId: def.visualId
  }
}
